import * as d3 from 'd3'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { chainVerdict, EMPTY_VERDICTS, isUnconfirmedVerdict, nodeKey } from './sca-dependency-chains.ts'

export const ScaVerdictSchema = z.enum(['exploitable', 'not exploitable', 'inconclusive', 'not evaluated'])
export type ScaVerdict = z.infer<typeof ScaVerdictSchema>
export type ScaVerdictMap = Map<string, ScaVerdict>

export const hasVerdicts = (verdicts: ScaVerdictMap): boolean => verdicts.size > 0

export type DependencyChainNode = {
  name: string
  version?: string | null
  node_type: 'root' | 'direct' | 'transitive' | 'vulnerable' | (string & {})
}

export type DependencyNodeDetail = {
  name: string
  shortName: string
  version: string | null
  node_type: string
  depth: number
  path: Array<{ name: string; shortName: string; version: string | null }>
  childCount: number
  descendantCount: number
  /**
   * For a vulnerable node, the verdict of the chain that reaches it; for any other node,
   * `'exploitable'` when it lies on an exploitable path. `null` when the report carries no
   * verdicts (or for a non-vulnerable node off every exploitable path).
   */
  verdict: ScaVerdict | null
}

export type TreeNode = {
  id: string
  name: string
  version?: string | null
  node_type: string
  children: TreeNode[]
}

// Reassign node_type by actual tree depth — chain index ≠ tree depth when chains
// have different starting packages and a synthetic root is inserted.
export function fixNodeTypes(node: TreeNode, depth: number) {
  if (depth > 0 && node.node_type !== 'vulnerable') {
    node.node_type = depth === 1 ? 'direct' : 'transitive'
  }
  for (const child of node.children) fixNodeTypes(child, depth + 1)
}

export function buildTree(chains: DependencyChainNode[][]): TreeNode | null {
  if (!chains.length) return null

  const synth: TreeNode = { id: '__root__', name: '', version: undefined, node_type: 'root', children: [] }

  for (const chain of chains) {
    let parent = synth
    for (let i = 0; i < chain.length; i++) {
      const node = chain[i]!
      const id = chain
        .slice(0, i + 1)
        .map(n => n.name)
        .join('>')
      let child = parent.children.find(c => c.id === id)
      if (!child) {
        child = { id, name: node.name, version: node.version, node_type: node.node_type, children: [] }
        parent.children.push(child)
      }
      parent = child
    }
  }

  const root = synth.children.length === 1 ? (synth.children[0] ?? synth) : synth
  fixNodeTypes(root, 0)
  if (root === synth) root.node_type = 'root'
  return root
}

const NODE_COLORS: Record<string, string> = {
  root: '#00E79A',
  direct: '#849cf5',
  transitive: '#aeabb6',
  vulnerable: '#f64555',
}

const STROKE_COLORS: Record<string, string> = {
  root: '#28807e',
  direct: '#6b7cd4',
  transitive: '#8d8997',
  vulnerable: '#e00b1c',
}

const NON_EXPLOITABLE_VULN_COLOR = '#c97a3e'
const NON_EXPLOITABLE_VULN_STROKE = '#9a5a28'
// Inconclusive or not-yet-evaluated chains: neither confirmed exploitable nor ruled out.
const UNCONFIRMED_VULN_COLOR = NODE_COLORS.transitive!
const UNCONFIRMED_VULN_STROKE = STROKE_COLORS.transitive!

function nodeRadius(node_type: string) {
  return node_type === 'vulnerable' ? 10 : 7
}

function shortName(fullName: string): string {
  if (!fullName) return ''
  const colonParts = fullName.split(':')
  if (colonParts.length >= 2) return colonParts[colonParts.length - 1]!
  if (fullName.includes('/')) {
    const parts = fullName.split('/')
    let last = parts[parts.length - 1]!
    if (/^v\d+$/.test(last) && parts.length > 1) last = parts[parts.length - 2]!
    return last
  }
  return fullName
}

function pathChain(node: d3.HierarchyNode<TreeNode>): DependencyChainNode[] {
  return node
    .ancestors()
    .reverse()
    .filter(a => a.data.name !== '')
    .map(a => ({ name: a.data.name, version: a.data.version, node_type: a.data.node_type }))
}

/** The verdict for the chain ending at a vulnerable leaf; unlabeled chains count as not evaluated. */
function leafVerdict(node: d3.HierarchyNode<TreeNode>, verdicts: ScaVerdictMap): ScaVerdict {
  return chainVerdict(pathChain(node), verdicts) ?? 'not evaluated'
}

/** The verdict the node panel badges: a vulnerable leaf's chain verdict, or `exploitable` for a node on an exploitable path. */
function detailVerdict({
  node,
  verdicts,
  vulnPathNodeIds,
}: {
  node: d3.HierarchyNode<TreeNode>
  verdicts: ScaVerdictMap
  vulnPathNodeIds: Set<string>
}): ScaVerdict | null {
  if (!hasVerdicts(verdicts)) return null
  if (node.data.node_type === 'vulnerable') return leafVerdict(node, verdicts)
  return vulnPathNodeIds.has(node.data.id) ? 'exploitable' : null
}

export function buildNodeDetail({
  node,
  verdicts,
  vulnPathNodeIds,
}: {
  node: d3.HierarchyNode<TreeNode>
  verdicts: ScaVerdictMap
  vulnPathNodeIds: Set<string>
}): DependencyNodeDetail {
  const ancestors = node
    .ancestors()
    .reverse()
    .filter(a => a.data.name !== '')
  return {
    name: node.data.name,
    shortName: shortName(node.data.name),
    version: node.data.version ?? null,
    node_type: node.data.node_type,
    depth: node.depth,
    path: ancestors.map(a => ({
      name: a.data.name,
      shortName: shortName(a.data.name),
      version: a.data.version ?? null,
    })),
    childCount: node.children?.length ?? 0,
    descendantCount: node.descendants().length - 1,
    verdict: detailVerdict({ node, verdicts, vulnPathNodeIds }),
  }
}

/**
 * Wheel zooms only with Ctrl/⌘ (trackpad pinch also sets ctrlKey); a plain wheel scrolls
 * the drawer instead of silently zooming the graph out of its fitted view.
 */
export function zoomFilter(event: { type: string; ctrlKey: boolean; metaKey: boolean; button: number }): boolean {
  if (event.type === 'wheel') return event.ctrlKey || event.metaKey
  return !event.ctrlKey && !event.button
}

function linkKey(a: d3.HierarchyPointNode<TreeNode>, b: d3.HierarchyPointNode<TreeNode>) {
  return `${a.x},${a.y}=>${b.x},${b.y}`
}

function computeVulnPaths(root: d3.HierarchyPointNode<TreeNode>, verdicts: ScaVerdictMap) {
  const nodeIds = new Set<string>()
  const links = new Set<string>()
  const unconfirmedLeafIds = new Set<string>()
  const withVerdicts = hasVerdicts(verdicts)

  root.descendants().forEach(node => {
    if (node.data.node_type !== 'vulnerable') return

    // Without verdict data highlight every path to the vulnerable leaf (fallback).
    // With verdicts, only highlight the paths whose chain is exploitable — other chains
    // reach a vulnerable leaf too, but their paths stay grey.
    const verdict = withVerdicts ? leafVerdict(node, verdicts) : undefined
    if (isUnconfirmedVerdict(verdict)) unconfirmedLeafIds.add(node.data.id)

    if (!withVerdicts || verdict === 'exploitable') {
      let cur: d3.HierarchyPointNode<TreeNode> | null = node
      while (cur) {
        nodeIds.add(cur.data.id)
        if (cur.parent) links.add(linkKey(cur.parent, cur))
        cur = cur.parent
      }
    }
  })
  return { nodeIds, links, unconfirmedLeafIds }
}

function applyFinalStyling(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  vulnPathNodeIds: Set<string>,
  vulnPathLinks: Set<string>,
  unconfirmedLeafIds: Set<string>,
  animated = true
) {
  const dur = animated ? 500 : 0

  // Clear dasharray so traced lines stay fully drawn
  g.selectAll<SVGPathElement, unknown>('.link').attr('stroke-dasharray', null).attr('stroke-dashoffset', null)

  g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node')
    .select<SVGCircleElement>('circle:not(.pulse-ring)')
    .transition()
    .duration(dur)
    .attr('fill', d => {
      if (vulnPathNodeIds.has(d.data.id)) return NODE_COLORS[d.data.node_type] ?? '#aeabb6'
      if (unconfirmedLeafIds.has(d.data.id)) return UNCONFIRMED_VULN_COLOR
      if (d.data.node_type === 'vulnerable') return NON_EXPLOITABLE_VULN_COLOR
      return NODE_COLORS[d.data.node_type] ?? '#aeabb6'
    })
    .attr('stroke', d => {
      if (vulnPathNodeIds.has(d.data.id)) return STROKE_COLORS[d.data.node_type] ?? '#8d8997'
      if (unconfirmedLeafIds.has(d.data.id)) return UNCONFIRMED_VULN_STROKE
      if (d.data.node_type === 'vulnerable') return NON_EXPLOITABLE_VULN_STROKE
      return STROKE_COLORS[d.data.node_type] ?? '#8d8997'
    })
    .attr('opacity', d => {
      if (vulnPathNodeIds.has(d.data.id)) return 1
      if (d.data.node_type === 'vulnerable') return 1
      return 0.65
    })

  // Non-exploitable vulnerable nodes get an amber pulse ring; unconfirmed ones a grey one
  g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node')
    .filter(d => d.data.node_type === 'vulnerable' && !vulnPathNodeIds.has(d.data.id))
    .select<SVGCircleElement>('.pulse-ring')
    .attr('stroke', d => (unconfirmedLeafIds.has(d.data.id) ? UNCONFIRMED_VULN_COLOR : NON_EXPLOITABLE_VULN_COLOR))

  g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node')
    .selectAll<SVGTextElement, d3.HierarchyPointNode<TreeNode>>('.node-label')
    .transition()
    .duration(dur)
    .attr('fill', '#fbfafb')
    .attr('opacity', (_d, i, nodes) => (nodes[i]?.classList.contains(OUTER_LABEL_CLASS) ? 1 : 0))

  g.selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>('.link')
    .transition()
    .duration(dur)
    .attr('stroke', d => {
      const src = d.source as d3.HierarchyPointNode<TreeNode>
      const tgt = d.target as d3.HierarchyPointNode<TreeNode>
      return vulnPathLinks.has(linkKey(src, tgt)) ? '#ff438c' : '#ffffff'
    })
    .attr('stroke-width', d => {
      const src = d.source as d3.HierarchyPointNode<TreeNode>
      const tgt = d.target as d3.HierarchyPointNode<TreeNode>
      return vulnPathLinks.has(linkKey(src, tgt)) ? 2.5 : 1
    })
    .attr('opacity', d => {
      const src = d.source as d3.HierarchyPointNode<TreeNode>
      const tgt = d.target as d3.HierarchyPointNode<TreeNode>
      return vulnPathLinks.has(linkKey(src, tgt)) ? 0.85 : 0.5
    })
}

function showFinalState(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  root: d3.HierarchyPointNode<TreeNode>,
  W: number,
  H: number,
  vulnPathNodeIds: Set<string>,
  vulnPathLinks: Set<string>,
  unconfirmedLeafIds: Set<string>
) {
  const transform = fitTransform(root, W, H, labelExtents(outerLabelSides(root)))
  const svgFontSize = Math.round(12 / transform.k)
  // eslint-disable-next-line @typescript-eslint/unbound-method
  svg.call(zoom.transform, transform)
  g.selectAll<SVGPathElement, unknown>('.link').attr('stroke-dasharray', null).attr('stroke-dashoffset', null)
  g.selectAll<SVGTextElement, d3.HierarchyPointNode<TreeNode>>('.node-label')
    .text(d => nodeLabel(d.data))
    .attr('font-size', svgFontSize)
    .attr('opacity', 1)
  g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node')
    .select('circle:not(.pulse-ring)')
    .attr('opacity', 1)
  g.selectAll<SVGGElement, unknown>('.pulse-ring')
    .style('visibility', 'visible')
    .style('animation', 'sca-pulse 1.8s ease-in-out infinite')
  applyFinalStyling(g, vulnPathNodeIds, vulnPathLinks, unconfirmedLeafIds, false)
}

type ScaDependencyTreeProps = {
  chains: DependencyChainNode[][]
  verdicts?: ScaVerdictMap
  width?: number
  height?: number
  autoPlay?: boolean
  onAnimationComplete?: () => void
  interactive?: boolean
  onNodeClick?: (detail: DependencyNodeDetail) => void
}

const LABEL_MAX_CHARS = 22
const OUTER_LABEL_CLASS = 'outer-label'
// On-screen radius of a node's click target, independent of the fitted zoom.
const HIT_RADIUS_PX = 12
const HOVER_SCALE = 1.5
// Inconsolata advances 0.5em per glyph, 6px at the 12px on-screen label size; the extra 0.5px is
// deliberate slack. An estimate, because the room must be reserved before any label is rendered.
const LABEL_CHAR_PX = 6.5
const LABEL_GAP_PX = 14

function nodeLabel(data: TreeNode): string {
  const label = nodeKey({ name: shortName(data.name), version: data.version })
  return label.length > LABEL_MAX_CHARS ? label.slice(0, LABEL_MAX_CHARS - 1) + '…' : label
}

export type LabelSide = 'left' | 'right'

// Each depth row labels only its leftmost and rightmost node (see outerLabelSides).
const LABELED_NODES_PER_ROW = 2

const LABEL_PLACEMENT: Record<LabelSide, { direction: number; anchor: 'start' | 'end' }> = {
  left: { direction: -1, anchor: 'end' },
  right: { direction: 1, anchor: 'start' },
}

/**
 * Whether the graph for `chains` leaves any node unlabeled: some depth row holds more nodes than
 * the outer two that get a label. The drawer uses it to decide whether to say so.
 */
export function hasUnlabeledNodes(chains: DependencyChainNode[][]) {
  const tree = buildTree(chains)
  if (!tree) return false
  const rowWidths: number[] = []
  const walk = (node: TreeNode, depth: number) => {
    rowWidths[depth] = (rowWidths[depth] ?? 0) + 1
    for (const child of node.children) walk(child, depth + 1)
  }
  walk(tree, 0)
  return rowWidths.some(width => width > LABELED_NODES_PER_ROW)
}

/**
 * Picks the nodes that keep a label: the leftmost and rightmost node of each depth row,
 * labelled outward so they never collide with a sibling. A row with one node labels it
 * on the right.
 */
export function outerLabelSides(
  root: d3.HierarchyPointNode<TreeNode>
): Map<d3.HierarchyPointNode<TreeNode>, LabelSide> {
  const sides = new Map<d3.HierarchyPointNode<TreeNode>, LabelSide>()
  for (const row of d3.group(root.descendants(), d => d.depth).values()) {
    const sorted = [...row].sort((a, b) => a.x - b.x)
    const first = sorted[0]
    const last = sorted[sorted.length - 1]
    if (last) sides.set(last, 'right')
    if (first && first !== last) sides.set(first, 'left')
  }
  return sides
}

/** On-screen room the outward labels need beyond the leftmost and rightmost nodes. */
export function labelExtents(sides: Map<d3.HierarchyPointNode<TreeNode>, LabelSide>): {
  left: number
  right: number
} {
  const widest = (side: LabelSide) =>
    Math.max(
      0,
      ...[...sides].filter(([, s]) => s === side).map(([d]) => nodeLabel(d.data).length * LABEL_CHAR_PX + LABEL_GAP_PX)
    )
  return { left: widest('left'), right: widest('right') }
}
// Used when the container has not been laid out yet (offsetWidth 0).
const DEFAULT_WIDTH_PX = 460

export const ScaDependencyTree: React.FC<ScaDependencyTreeProps> = ({
  chains,
  verdicts = EMPTY_VERDICTS,
  width,
  height = 220,
  autoPlay = true,
  onAnimationComplete,
  interactive = false,
  onNodeClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const resetZoomRef = useRef<(() => void) | null>(null)
  const onAnimationCompleteRef = useRef(onAnimationComplete)
  onAnimationCompleteRef.current = onAnimationComplete
  const onNodeClickRef = useRef(onNodeClick)
  onNodeClickRef.current = onNodeClick

  useEffect(() => {
    const svgEl = svgRef.current
    const tooltipEl = tooltipRef.current
    if (!svgEl || !tooltipEl) return
    const svg = d3.select(svgEl)
    svg.selectAll('*').remove()

    const treeData = buildTree(chains)
    if (!treeData) return

    const W = width ?? (containerRef.current?.offsetWidth || DEFAULT_WIDTH_PX)
    const H = height

    const g = svg.append('g')

    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.05, 8])
      .filter(zoomFilter)
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        g.attr('transform', event.transform.toString())
        if (interactive) {
          svg.style('cursor', (event.sourceEvent as Event | null)?.type === 'mousemove' ? 'grabbing' : 'grab')
        }
      })
    svg.call(zoomBehavior)
    if (!interactive) {
      svg.on('wheel.zoom', null)
    } else {
      svg.style('cursor', 'grab')
      svg.on('mousedown.grab', () => svg.style('cursor', 'grabbing'))
      svg.on('mouseup.grab', () => svg.style('cursor', 'grab'))
    }

    const { layoutRoot, svgW } = layoutTree(treeData, W, H)

    svg.attr('width', svgW).attr('height', H)

    const {
      nodeIds: vulnPathNodeIds,
      links: vulnPathLinks,
      unconfirmedLeafIds,
    } = computeVulnPaths(layoutRoot, verdicts)
    const labelSides = outerLabelSides(layoutRoot)

    const initialTransform = fitTransform(layoutRoot, W, H, labelExtents(labelSides))
    const fitS = initialTransform.k
    // eslint-disable-next-line @typescript-eslint/unbound-method
    svg.call(zoomBehavior.transform, initialTransform)
    // eslint-disable-next-line @typescript-eslint/unbound-method
    resetZoomRef.current = () => svg.transition().duration(400).call(zoomBehavior.transform, initialTransform)
    const svgFontSize = Math.round(12 / fitS)

    // Links — invisible initially; brighten as logos trace them
    g.selectAll('.link')
      .data(layoutRoot.links())
      .join('path')
      .attr('class', 'link')
      .attr(
        'd',
        d3
          .linkVertical<d3.HierarchyLink<TreeNode>, d3.HierarchyPointNode<TreeNode>>()
          .x(d => d.x)
          .y(d => d.y)
      )
      .attr('fill', 'none')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5)
      .attr('opacity', 0)

    // Nodes — circles start dim, labels hidden; revealed by animation
    const nodeGroups = g
      .selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.node')
      .data(layoutRoot.descendants())
      .join('g')
      .attr('class', d => `sca-node ${d.data.node_type}`)
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('cursor', 'default')

    nodeGroups
      .append('circle')
      .attr('r', d => nodeRadius(d.data.node_type))
      .attr('fill', d => NODE_COLORS[d.data.node_type] ?? '#aeabb6')
      .attr('stroke', d => STROKE_COLORS[d.data.node_type] ?? '#8d8997')
      .attr('stroke-width', 2)
      .attr('opacity', 0) // revealed on arrival

    // Pulse ring on vulnerable — hidden until arrival; animation deferred so the
    // CSS keyframe opacity doesn't override the SVG attr before the node is revealed
    nodeGroups
      .filter(d => d.data.node_type === 'vulnerable')
      .append('circle')
      .attr('class', 'pulse-ring')
      .attr('r', 10)
      .attr('fill', 'none')
      .attr('stroke', '#f64555')
      .attr('stroke-width', 1.5)
      .style('visibility', 'hidden')

    // Labels — hidden until logo arrives. Only the outermost node of each row keeps a
    // label (left of the leftmost, right of the rightmost) so siblings never overlap;
    // an interior node shows its label above itself while it is selected.
    nodeGroups
      .append('text')
      .attr('class', 'node-label')
      .classed(OUTER_LABEL_CLASS, d => labelSides.has(d))
      .attr('dy', d => (labelSides.has(d) ? '0.35em' : -nodeRadius(d.data.node_type) - 6))
      .attr('dx', d => {
        const side = labelSides.get(d)
        return side ? LABEL_PLACEMENT[side].direction * (nodeRadius(d.data.node_type) + 8) : 0
      })
      .attr('text-anchor', d => {
        const side = labelSides.get(d)
        return side ? LABEL_PLACEMENT[side].anchor : 'middle'
      })
      .attr('font-size', svgFontSize)
      .attr('font-family', 'Inconsolata, monospace')
      .attr('fill', '#fbfafb')
      .attr('pointer-events', 'none')
      .attr('opacity', 0) // revealed on arrival
      .text(d => nodeLabel(d.data))

    if (interactive) {
      addHoverTargets(nodeGroups, fitS)

      nodeGroups.style('cursor', 'pointer').on('click', (_event: MouseEvent, d) => {
        void panTo(svg, zoomBehavior, d, W, H, 2.5, 500)
        // Keep the outer labels and reveal the clicked node's label
        g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node')
          .selectAll<SVGTextElement, d3.HierarchyPointNode<TreeNode>>('.node-label')
          .attr('opacity', (nd, i, nodes) => (nd === d || nodes[i]?.classList.contains(OUTER_LABEL_CLASS) ? 1 : 0))
        onNodeClickRef.current?.(buildNodeDetail({ node: d, verdicts, vulnPathNodeIds }))
      })
    } else {
      nodeGroups
        .on('mouseenter', (event: MouseEvent, d) => {
          const full = nodeKey({ name: shortName(d.data.name), version: d.data.version })
          tooltipEl.textContent = full
          tooltipEl.style.display = 'block'
          tooltipEl.style.left = `${event.clientX + 12}px`
          tooltipEl.style.top = `${event.clientY - 32}px`
        })
        .on('mousemove', (event: MouseEvent) => {
          tooltipEl.style.left = `${event.clientX + 12}px`
          tooltipEl.style.top = `${event.clientY - 32}px`
        })
        .on('mouseleave', () => {
          tooltipEl.style.display = 'none'
        })
    }

    const autoSelectVulnNode = () => {
      const target =
        layoutRoot.descendants().find(d => d.data.node_type === 'vulnerable' && vulnPathNodeIds.has(d.data.id)) ??
        layoutRoot.descendants().find(d => d.data.node_type === 'vulnerable')
      if (!target) return
      onNodeClickRef.current?.(buildNodeDetail({ node: target, verdicts, vulnPathNodeIds }))
    }

    if (!autoPlay) {
      // The viewport width W, not the canvas width svgW: see fitTransform.
      showFinalState(svg, g, zoomBehavior, layoutRoot, W, H, vulnPathNodeIds, vulnPathLinks, unconfirmedLeafIds)
      autoSelectVulnNode()
      return () => {
        tooltipEl.style.display = 'none'
      }
    }

    const cancelled = { value: false }
    void runWaterflowAnimation(g, layoutRoot, cancelled, vulnPathNodeIds, vulnPathLinks, unconfirmedLeafIds).then(
      () => {
        if (!cancelled.value) {
          onAnimationCompleteRef.current?.()
          autoSelectVulnNode()
        }
      }
    )

    return () => {
      cancelled.value = true
      tooltipEl.style.display = 'none'
    }
  }, [chains, verdicts, width, height, autoPlay, interactive])

  return (
    <div ref={containerRef} style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', position: 'relative' }}>
      <style>{`
        @keyframes sca-pulse {
          0%, 100% { r: 10px; opacity: 0.6; }
          50%       { r: 16px; opacity: 0;   }
        }
      `}</style>
      <svg ref={svgRef} style={{ display: 'block', margin: '0 auto' }} />
      {interactive && (
        <button
          aria-label="Reset zoom"
          onClick={() => resetZoomRef.current?.()}
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#2c2541',
            border: '1px solid #3d3751',
            borderRadius: 6,
            cursor: 'pointer',
            color: '#8b8599',
            padding: 0,
            transition: 'color 0.15s, background 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#3a3356'
            e.currentTarget.style.color = '#c4c0d0'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = '#2c2541'
            e.currentTarget.style.color = '#8b8599'
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 4V1H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 1H13V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M13 10V13H10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M4 13H1V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      <div
        ref={tooltipRef}
        style={{
          display: 'none',
          position: 'fixed',
          background: '#2c2541',
          border: '1px solid #3d3751',
          borderRadius: '6px',
          padding: '5px 9px',
          fontSize: '12px',
          fontFamily: 'Inconsolata, monospace',
          color: '#fbfafb',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      />
    </div>
  )
}

// ── Legend ────────────────────────────────────────────────────────────────────

function buildLegendItems(
  chains: DependencyChainNode[][],
  verdicts: ScaVerdictMap
): Array<{ color: string; label: string }> {
  const presentTypes = new Set(chains.flat().map(n => n.node_type))
  const items: Array<{ color: string; label: string }> = []

  if (presentTypes.has('vulnerable')) {
    if (!hasVerdicts(verdicts)) {
      items.push({ color: '#f64555', label: 'Vulnerable dependency' })
    } else {
      const chainVerdicts = chains
        .filter(chain => chain.some(n => n.node_type === 'vulnerable'))
        .map(chain => chainVerdict(chain, verdicts) ?? 'not evaluated')
      if (chainVerdicts.includes('exploitable')) items.push({ color: '#f64555', label: 'Exploitable dependency' })
      if (chainVerdicts.includes('not exploitable'))
        items.push({ color: NON_EXPLOITABLE_VULN_COLOR, label: 'Non-exploitable dependency' })
      if (chainVerdicts.some(isUnconfirmedVerdict))
        items.push({ color: UNCONFIRMED_VULN_COLOR, label: 'Unconfirmed dependency' })
    }
  }

  if (presentTypes.has('root')) items.push({ color: '#00E79A', label: 'Project root' })
  if (presentTypes.has('direct')) items.push({ color: '#849cf5', label: 'Direct dependency' })
  if (presentTypes.has('transitive')) items.push({ color: '#aeabb6', label: 'Transitive dependency' })

  return items
}

export const ScaDependencyLegend: React.FC<{
  chains: DependencyChainNode[][]
  verdicts?: ScaVerdictMap
  dotFirst?: boolean
}> = ({ chains, verdicts = EMPTY_VERDICTS, dotFirst = false }) => {
  const items = buildLegendItems(chains, verdicts)
  if (!items.length) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: dotFirst ? 'flex-start' : 'flex-end' }}>
      {items.map(({ color, label }) => (
        <div
          key={label}
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: dotFirst ? 'row' : 'row-reverse' }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontFamily: 'Poppins, sans-serif', color: '#aeabb6' }}>{label}</span>
        </div>
      ))}
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function findLinkEl(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  parent: d3.HierarchyPointNode<TreeNode>,
  child: d3.HierarchyPointNode<TreeNode>
): SVGPathElement | null {
  return g
    .selectAll<SVGPathElement, d3.HierarchyLink<TreeNode>>('.link')
    .filter(d => d.source === parent && d.target === child)
    .node()
}

function animateAlongPath(
  dot: d3.Selection<SVGGElement, unknown, null, undefined>,
  pathEl: SVGPathElement | null,
  forward: boolean,
  duration: number
): Promise<void> {
  return new Promise(resolve => {
    if (!pathEl) {
      resolve()
      return
    }
    const len = pathEl.getTotalLength()
    dot
      .transition()
      .duration(duration)
      .ease(d3.easeQuadInOut)
      .tween('follow', () => t => {
        const pt = pathEl.getPointAtLength((forward ? t : 1 - t) * len)
        dot.attr('transform', `translate(${pt.x},${pt.y})`)
      })
      .on('end', () => resolve())
  })
}

function panTo(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  node: d3.HierarchyPointNode<TreeNode>,
  W: number,
  H: number,
  scale: number,
  duration: number
): Promise<void> {
  return new Promise(resolve => {
    svg
      .transition()
      .duration(duration)
      .ease(d3.easeQuadInOut)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .call(zoom.transform, d3.zoomIdentity.translate(W / 2 - node.x * scale, H / 2 - node.y * scale).scale(scale))
      .on('end', () => resolve())
  })
}

// Minimum horizontal distance between sibling nodes, and vertical distance between depths, before fitting.
const MIN_PITCH_PX = 90
const ROW_PITCH_PX = 150

export function layoutTree(treeData: TreeNode, W: number, H: number) {
  const hierarchyRoot = d3.hierarchy(treeData)

  const breadthByDepth = new Array<number>(hierarchyRoot.height + 1).fill(0)
  hierarchyRoot.each(d => {
    breadthByDepth[d.depth] = (breadthByDepth[d.depth] ?? 0) + 1
  })
  const maxBreadth = Math.max(...breadthByDepth.map(n => n ?? 0))

  const pad = 80
  const svgW = Math.max(W, maxBreadth * MIN_PITCH_PX + pad)
  const treeW = svgW - pad
  const treeH = Math.max(H - 60, (hierarchyRoot.height + 1) * ROW_PITCH_PX)

  const layoutRoot = d3
    .tree<TreeNode>()
    .size([treeW, treeH])
    .separation(() => 1)(hierarchyRoot)
  return { layoutRoot, svgW }
}

export function fitScale(
  root: d3.HierarchyPointNode<TreeNode>,
  W: number,
  H: number,
  labelPx: { left: number; right: number } = { left: 0, right: 0 }
) {
  const pad = 40
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity
  for (const d of root.descendants()) {
    if (d.x < minX) minX = d.x
    if (d.x > maxX) maxX = d.x
    if (d.y < minY) minY = d.y
    if (d.y > maxY) maxY = d.y
  }
  // Reserve room for the outward labels, but never more than half the frame. When the floor
  // binds, both sides give up the same share, so any overflow is split evenly instead of all
  // landing on the side with the wider label.
  const frameW = W - pad * 2
  const wantedLabelW = labelPx.left + labelPx.right
  const reservedLabelW = Math.min(wantedLabelW, frameW / 2)
  const reservedShare = wantedLabelW ? reservedLabelW / wantedLabelW : 0
  const scale = Math.min((frameW - reservedLabelW) / (maxX - minX || 1), (H - pad * 2) / (maxY - minY || 1), 2)
  const labelShift = ((labelPx.right - labelPx.left) * reservedShare) / (2 * scale)
  return { scale, cx: (minX + maxX) / 2 + labelShift, cy: (minY + maxY) / 2 }
}

/**
 * Grows a node on hover and gives it a transparent click target of a fixed on-screen size,
 * so nodes stay easy to click when the fitted graph is scaled down. The target is appended
 * last so `.select('circle')` still finds the node circle.
 */
function addHoverTargets(
  nodeGroups: d3.Selection<SVGGElement, d3.HierarchyPointNode<TreeNode>, SVGGElement, unknown>,
  fitScaleValue: number
) {
  nodeGroups
    .append('circle')
    .attr('class', 'hit-area')
    .attr('r', d => Math.max(nodeRadius(d.data.node_type) + 4, HIT_RADIUS_PX / fitScaleValue))
    .attr('fill', 'transparent')

  const resize = (group: SVGGElement, r: number) =>
    d3.select(group).select<SVGCircleElement>('circle:not(.pulse-ring)').transition().duration(120).attr('r', r)

  nodeGroups
    .on('mouseenter', function (this: SVGGElement, _event: MouseEvent, d) {
      resize(this, nodeRadius(d.data.node_type) * HOVER_SCALE)
    })
    .on('mouseleave', function (this: SVGGElement, _event: MouseEvent, d) {
      resize(this, nodeRadius(d.data.node_type))
    })
}

/**
 * The zoom transform that fits `root` into the W×H viewport. It centres on the viewport width W, not
 * the SVG canvas width from `layoutTree`: a fan-out canvas is wider than the frame, and centring on
 * the canvas pushed the graph off-screen.
 */
export function fitTransform(
  root: d3.HierarchyPointNode<TreeNode>,
  W: number,
  H: number,
  labelPx?: { left: number; right: number }
) {
  const { scale, cx, cy } = fitScale(root, W, H, labelPx)
  return d3.zoomIdentity.translate(W / 2 - cx * scale, H / 2 - cy * scale).scale(scale)
}

// Reveal a node: brighten the circle, fade in the label and root badge
function revealNode(g: d3.Selection<SVGGElement, unknown, null, undefined>, node: d3.HierarchyPointNode<TreeNode>) {
  const group = g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node').filter(d => d === node)

  group.select<SVGCircleElement>('circle:not(.pulse-ring)').transition().duration(180).attr('opacity', 1)
  group.select<SVGTextElement>(`.node-label.${OUTER_LABEL_CLASS}`).transition().duration(180).attr('opacity', 1)

  // Reveal pulse ring and start its CSS animation only on arrival so the keyframe
  // opacity can't bleed through before the node is shown
  group
    .select<SVGCircleElement>('.pulse-ring')
    .style('visibility', 'visible')
    .style('animation', 'sca-pulse 1.8s ease-in-out infinite')
}

// Brief scale-bounce on a node circle when the logo arrives
function bounceNode(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  node: d3.HierarchyPointNode<TreeNode>
): Promise<void> {
  return new Promise(resolve => {
    const r = nodeRadius(node.data.node_type)
    const bounce = node.data.node_type === 'vulnerable' ? r + 6 : r + 4
    g.selectAll<SVGGElement, d3.HierarchyPointNode<TreeNode>>('.sca-node')
      .filter(d => d === node)
      .select('circle')
      .transition()
      .duration(140)
      .attr('r', bounce)
      .transition()
      .duration(140)
      .attr('r', r)
      .on('end', () => resolve())
  })
}

// ── Waterflow animation ───────────────────────────────────────────────────────
// Tree starts hidden. Root appears, then one Pixee logo per branch fans out.
// Each logo draws its link (stroke-dashoffset) and reveals nodes on arrival.
// All branches are time-normalised: longer paths get a shorter per-edge duration
// so every root-to-leaf path finishes at the same wall-clock time.

const BASE_TRACE_MS = 650 // per-edge duration for the longest path
const ARRIVAL_BOUNCE_MS = 90

// Maximum edges from this node to any leaf
function maxLeafDepth(node: d3.HierarchyPointNode<TreeNode>): number {
  return node.children?.length ? 1 + Math.max(...node.children.map(maxLeafDepth)) : 0
}

async function traceEdge(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  from: d3.HierarchyPointNode<TreeNode>,
  to: d3.HierarchyPointNode<TreeNode>,
  cancelled: { value: boolean },
  edgeDur: number // pre-computed so all branches finish at the same time
): Promise<void> {
  const dotEl = document.createElementNS('http://www.w3.org/2000/svg', 'g')
  g.node()!.appendChild(dotEl)
  const dot = d3
    .select<SVGGElement, unknown>(dotEl)
    .attr('transform', `translate(${from.x},${from.y})`)
    .attr('opacity', 0.95) // set immediately — a prior transition cancels animateAlongPath

  dot
    .append('image')
    .attr('href', '/0-Icon-Trimmed.png')
    .attr('x', -14)
    .attr('y', -14)
    .attr('width', 28)
    .attr('height', 28)
    .attr('preserveAspectRatio', 'xMidYMid meet')

  const pathEl = findLinkEl(g, from, to)
  if (pathEl) {
    const len = pathEl.getTotalLength()
    d3.select(pathEl)
      .attr('stroke-dasharray', `${len} ${len}`)
      .attr('stroke-dashoffset', len)
      .attr('stroke-width', 2)
      .attr('opacity', 0.5)
      .transition()
      .duration(edgeDur)
      .ease(d3.easeLinear)
      .attr('stroke-dashoffset', 0)
  }

  await animateAlongPath(dot, pathEl, true, edgeDur)
  if (cancelled.value) {
    dot.remove()
    return
  }

  // Show the node as soon as the line reaches it, not after the logo bounce
  revealNode(g, to)

  // Logo bounce on arrival
  await new Promise<void>(resolve => {
    dot
      .transition()
      .duration(ARRIVAL_BOUNCE_MS)
      .attr('transform', `translate(${to.x},${to.y}) scale(1.35)`)
      .transition()
      .duration(ARRIVAL_BOUNCE_MS)
      .attr('transform', `translate(${to.x},${to.y}) scale(1.0)`)
      .on('end', () => resolve())
  })

  await bounceNode(g, to)

  await new Promise<void>(resolve => {
    dot
      .transition()
      .duration(200)
      .attr('opacity', 0)
      .on('end', () => {
        dot.remove()
        resolve()
      })
  })
  if (cancelled.value) return

  await flowFromNode(g, to, cancelled)
}

async function flowFromNode(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  node: d3.HierarchyPointNode<TreeNode>,
  cancelled: { value: boolean }
): Promise<void> {
  if (!node.children?.length || cancelled.value) return
  // Compute totalAnimTime once from this node's max subtree depth so all child
  // branches finish at the same time regardless of their individual lengths.
  const subDepth = maxLeafDepth(node)
  const totalAnimTime = subDepth * BASE_TRACE_MS
  await Promise.all(
    node.children.map(child => {
      const pathLen = 1 + maxLeafDepth(child) // edges from this node through child to deepest leaf
      const edgeDur = totalAnimTime / pathLen
      return traceEdge(g, node, child, cancelled, edgeDur)
    })
  )
}

async function runWaterflowAnimation(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  root: d3.HierarchyPointNode<TreeNode>,
  cancelled: { value: boolean },
  vulnPathNodeIds: Set<string>,
  vulnPathLinks: Set<string>,
  unconfirmedLeafIds: Set<string>
) {
  revealNode(g, root)
  await new Promise(r => setTimeout(r, 320))
  if (cancelled.value) return

  await flowFromNode(g, root, cancelled)
  if (cancelled.value) return

  applyFinalStyling(g, vulnPathNodeIds, vulnPathLinks, unconfirmedLeafIds, true)
}
